import { Grid, IconButton, MenuItem, TextField } from '@mui/material';
import ClearableSelect from 'components/ClearableSelect/ClearableSelect';
import { produce } from 'immer';
import { useTranslation } from 'react-i18next';
import { useRecoilState } from 'recoil';
import { filterState } from 'state/exportState';
import { set } from 'object-path';
import { Close } from '@mui/icons-material';
import { FormTemplateType, User } from 'api/models';
import ConditionOperationSelect from 'components/GlobalDialog/AddFieldTemplate/components/ConditionConfig/ConditionOperationSelect';
import { ConditionValueType, ReferenceType } from 'api/models/FieldTemplate';
import { useState } from 'react';
import StatusSelect from '../StatusSelect';
import { isFieldEmpty } from 'utils';

interface FormFilterRowProps {
  index: number;
  formTemplates?: FormTemplateType[];
  users?: User[];
}

export default function FormFilterRow({
  index,
  formTemplates,
  users,
}: FormFilterRowProps) {
  const [filters, setFilters] = useRecoilState(filterState);
  const { t } = useTranslation();
  const filter = filters[index];
  const [formId, setFormId] = useState('');

  return (
    <Grid container spacing={1}>
      <Grid item xs={3}>
        <ClearableSelect
          label={t('export.formTemplates')}
          fullWidth
          value={formId}
          required
          error={!formId}
          helperText={
           formId ? '' : t('general.errors.mandatoryField')
          }
          onChange={event => {
            const formId = event.target.value;
            
            setFormId(formId);
            const updatedFilters = produce(filters, draft => {
              set(draft[index], 'reference.formTemplateId', formId);
            });

            setFilters(updatedFilters);
          }}
        >
          {formTemplates?.map(formTemplate => (
            <MenuItem key={formTemplate.id} value={formTemplate.id}>
              {formTemplate.name}
            </MenuItem>
          ))}
        </ClearableSelect>
      </Grid>
      <Grid item xs={1}>
        <ConditionOperationSelect
          operation={filter.operation}
          onChange={operation => {
            const updatedFilters = produce(filters, draft => {
              set(draft[index], 'operation', operation);
            });
            setFilters(updatedFilters);
          }}
          type={ConditionValueType.STRING_LIST}
        />
      </Grid>
      <Grid item xs={5.5}>
        <ClearableSelect
          label={t('visits.updatedBy')}
          required
          fullWidth
          multiple
          error={isFieldEmpty(filter.value)}
          value={filter.value}
          helperText={
            !isFieldEmpty(filter.value) ? '' : t('general.errors.mandatoryField')
          }
          onChange={e => {
            const updatedFilters = produce(filters, draft => {
              set(draft[index], 'reference.type', ReferenceType.formReference);
              set(draft[index], 'reference.attribute', 'updatedBy');
              set(draft[index], 'value', e.target.value);
            });
            setFilters(updatedFilters);
          }}
        >
          {users?.map(user => (
            <MenuItem key={user.id} value={user.email}>
              {`${user.firstName} ${user.lastName}`}
            </MenuItem>
          ))}
        </ClearableSelect>
      </Grid>
      <Grid item xs={1}>
        <StatusSelect
          value={filter.reference.formStatus}
          onChange={statuses => {
            const updatedFilters = produce(filters, draft => {
              set(draft[index], 'reference.formStatus', statuses);
            });
            setFilters(updatedFilters);
          }}
        />
      </Grid>
      <Grid item xs={1}>
        <TextField
          fullWidth
          label={t('export.visitNumber')}
          value={filter.reference.visitIndex}
          onChange={e => {
            const updatedFilters = produce(filters, draft => {
              set(draft[index], 'reference.visitIndex', e.target.value);
            });
            setFilters(updatedFilters);
          }}
        />
      </Grid>
      <Grid item xs={0.5}>
        <IconButton
          sx={{ mt: 2 }}
          onClick={() => {
            const updatedFilters = produce(filters, draft => {
              draft.splice(index, 1);
            });
            setFilters(updatedFilters);
          }}
          size="small"
        >
          <Close fontSize="small" />
        </IconButton>
      </Grid>
    </Grid>
  );
}
